import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Link,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Business from "@mui/icons-material/Business";
import Person from "@mui/icons-material/Person";
import CardMembership from "@mui/icons-material/CardMembership";
import ToggleOn from "@mui/icons-material/ToggleOn";
import ToggleOff from "@mui/icons-material/ToggleOff";
import {
  useGetAllCompanies,
  useUpdateCompanyStatus,
  useListCompanyUsersSuperAdmin,
  type CompanyUser,
} from "../../hooks/api/useCompanies";
import { useGetSubscriptionListSuperAdmin } from "../../hooks/api/useSubscription";
import type { Company } from "../../Graphql/types/company";
import type { Subscription } from "../../Graphql/types/subscription";
import {
  formatCompanyStatusLabel,
  formatSubscriptionStatusLabel,
} from "../common/adminEnumLabels";
import { ROUTE_COMPANIES } from "../../constants/routes";
import { responsiveHeading } from "../../styles/layoutStyles";

function formatAddress(addr: Company["address"]): string {
  if (!addr) return "—";
  const parts = [
    addr.streetAddress,
    addr.village,
    addr.cell,
    addr.sector,
    addr.district,
    addr.province,
    addr.country,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      dateStyle: "medium",
    });
  } catch {
    return iso;
  }
}

const CompanyDetailView: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [company, setCompany] = useState<Company | null>(null);
  const [adminAnchor, setAdminAnchor] = useState<HTMLElement | null>(null);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [confirmStatusOpen, setConfirmStatusOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<Company["status"] | null>(
    null,
  );

  const getAllCompanies = useGetAllCompanies();
  const updateStatus = useUpdateCompanyStatus();
  const listCompanyUsers = useListCompanyUsersSuperAdmin();
  const getSubscriptionListSuperAdmin = useGetSubscriptionListSuperAdmin();

  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const primaryAdmin =
    companyUsers.find((u) => u.role === "COMPANY_ADMIN") ??
    companyUsers[0] ??
    null;

  useEffect(() => {
    if (!companyId) return;
    getAllCompanies
      .mutateAsync()
      .then((res) => {
        const list = res.getAllCompaniesSuperAdmin.companies ?? [];
        const found = list.find((c) => c.id === companyId) ?? null;
        setCompany(found);
      })
      .catch(() => {});
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    listCompanyUsers
      .mutateAsync({ companyId })
      .then((res) => {
        const users = res.listCompanyUsersSuperAdmin?.users ?? [];
        setCompanyUsers(users);
      })
      .catch(() => {});
  }, [companyId]);

  const openConfirmStatus = () => {
    if (!company) return;
    const nextStatus = company.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    setPendingStatus(nextStatus);
    setConfirmStatusOpen(true);
  };

  const handleConfirmStatus = () => {
    if (!company || pendingStatus == null) return;
    updateStatus
      .mutateAsync({ companyId: company.id, status: pendingStatus })
      .then((res) => {
        const updated = res.updateCompanyStatus?.company;
        if (updated) setCompany(updated);
        setConfirmStatusOpen(false);
        setPendingStatus(null);
      })
      .catch(() => {});
  };

  const handleCloseConfirmStatus = () => {
    setConfirmStatusOpen(false);
    setPendingStatus(null);
  };

  const handleViewSubscription = () => {
    if (!companyId) return;
    getSubscriptionListSuperAdmin
      .mutateAsync({ companyId })
      .then((res) => {
        setSubscriptions(
          res.listSubscriptionsSuperAdmin?.subscriptionList ?? [],
        );
        setSubscriptionDialogOpen(true);
      })
      .catch(() => {});
  };

  const adminDisplayName = (u: CompanyUser) => {
    const first = [u.firstName, u.lastName].filter(Boolean).join(" ");
    return first || u.email || u.id;
  };

  if (!companyId) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography color="text.secondary">Missing company ID.</Typography>
      </Box>
    );
  }

  if (!getAllCompanies.isPending && !company) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography color="text.secondary">Company not found.</Typography>
        <Button
          startIcon={<ArrowBack />}
          sx={{ mt: 1 }}
          onClick={() => navigate(ROUTE_COMPANIES)}
        >
          Back to companies
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {/* Back navigation */}
      <IconButton
        onClick={() => navigate(ROUTE_COMPANIES)}
        sx={{ mb: 2, color: "text.secondary" }}
        aria-label="Back to companies"
      >
        <ArrowBack />
      </IconButton>

      {company && (
        <>
          {/* Hero header */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${theme.palette.background.paper} 100%)`,
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
              justifyContent="space-between"
            >
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                minWidth={0}
                flex={1}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Business sx={{ fontSize: 32 }} />
                </Box>
                <Box minWidth={0}>
                  <Typography
                    variant="h5"
                    sx={{ ...responsiveHeading, mb: 0.5 }}
                  >
                    {company.name}
                  </Typography>
                  <Chip
                    label={formatCompanyStatusLabel(company.status)}
                    size="small"
                    color={
                      company.status === "ACTIVE"
                        ? "success"
                        : company.status === "SUSPENDED"
                          ? "warning"
                          : "default"
                    }
                    sx={{ fontWeight: 600, textTransform: "uppercase" }}
                  />
                </Box>
              </Box>

              {/* Actions */}
              <Box
                display="flex"
                gap={1.5}
                flexWrap="wrap"
                justifyContent="flex-end"
                sx={{ flexShrink: 0 }}
              >
                <Button
                  variant="contained"
                  size="medium"
                  color={company.status === "ACTIVE" ? "warning" : "primary"}
                  startIcon={
                    company.status === "ACTIVE" ? <ToggleOff /> : <ToggleOn />
                  }
                  onClick={openConfirmStatus}
                  disabled={updateStatus.isPending}
                >
                  {company.status === "ACTIVE" ? "Deactivate" : "Activate"}
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<CardMembership />}
                  onClick={handleViewSubscription}
                  disabled={getSubscriptionListSuperAdmin.isPending}
                >
                  View subscription
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Details card */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Company details
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              gap={3}
            >
              <DetailItem label="Name" value={company.name} />
              <DetailItem
                label="Registration number"
                value={company.registrationNumber}
              />
              <Box sx={{ gridColumn: { xs: 1, sm: "1 / -1" } }}>
                <DetailItem label="ID" value={company.id} mono />
              </Box>
              <Box sx={{ gridColumn: { xs: 1, sm: "1 / -1" } }}>
                <DetailItem
                  label="Address"
                  value={formatAddress(company.address)}
                />
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5 }}
                >
                  Admin
                </Typography>
                {primaryAdmin ? (
                  <>
                    <Link
                      component="button"
                      variant="body1"
                      underline="hover"
                      onClick={(e) => setAdminAnchor(e.currentTarget)}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      <Person sx={{ fontSize: 18 }} />
                      {adminDisplayName(primaryAdmin)}
                    </Link>
                    <Popover
                      open={Boolean(adminAnchor)}
                      anchorEl={adminAnchor}
                      onClose={() => setAdminAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      PaperProps={{
                        sx: {
                          borderRadius: 2,
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                          minWidth: 260,
                          overflow: "hidden",
                        },
                      }}
                    >
                      <Box sx={{ p: 2.5, bgcolor: "grey.50" }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          textTransform="uppercase"
                          fontWeight={600}
                        >
                          Company admin
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          fontWeight={600}
                          sx={{ mt: 0.5 }}
                        >
                          {adminDisplayName(primaryAdmin)}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 2.5 }}>
                        {primaryAdmin.email && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Email:</strong> {primaryAdmin.email}
                          </Typography>
                        )}
                        {primaryAdmin.phone && (
                          <Typography variant="body2">
                            <strong>Phone:</strong> {primaryAdmin.phone}
                          </Typography>
                        )}
                      </Box>
                    </Popover>
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    —
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>
        </>
      )}

      {/* Subscription dialog */}
      <Dialog
        open={subscriptionDialogOpen}
        onClose={() => setSubscriptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Subscription details</DialogTitle>
        <DialogContent>
          {subscriptions.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No subscriptions for this company.
            </Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              {subscriptions.map((s) => (
                <Paper
                  key={s.subscriptionId}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderColor: "divider",
                    bgcolor: "grey.50",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="primary"
                    gutterBottom
                  >
                    {s.planName}
                  </Typography>
                  <Chip
                    label={formatSubscriptionStatusLabel(s.status)}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2">
                      Starts: {formatDate(s.subscriptionStartsAt)}
                    </Typography>
                    <Typography variant="body2">
                      Ends: {formatDate(s.subscriptionEndsAt)}
                    </Typography>
                    {s.priceAmount != null && (
                      <Typography variant="body2" fontWeight={500}>
                        {s.priceAmount} {s.priceCurrency ?? ""}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm activate/deactivate */}
      <Dialog
        open={confirmStatusOpen}
        onClose={handleCloseConfirmStatus}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {pendingStatus === "ACTIVE"
            ? "Activate company?"
            : "Deactivate company?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {company &&
              (pendingStatus === "ACTIVE"
                ? `Are you sure you want to activate "${company.name}"? The company will be able to use the platform again.`
                : `Are you sure you want to deactivate "${company.name}"? The company will be suspended and may lose access.`)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseConfirmStatus} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pendingStatus === "ACTIVE" ? "primary" : "warning"}
            onClick={handleConfirmStatus}
            disabled={updateStatus.isPending}
          >
            {updateStatus.isPending
              ? "Updating…"
              : pendingStatus === "ACTIVE"
                ? "Activate"
                : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function DetailItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontFamily: mono ? "monospace" : undefined,
          fontSize: mono ? "0.875rem" : undefined,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

export default CompanyDetailView;
